import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Zap, Cpu, MessageCircle, ArrowUpRight, Wrench, ShoppingBag } from 'lucide-react';
import { motion } from 'framer-motion';
import VideoReels from '@/components/VideoReels';

const ease = [0.16, 1, 0.3, 1];

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 md:py-32 lg:py-40" data-testid="hero-section">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl relative z-10">
          {/* Mobile: horizontal video reel */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease }}
            className="lg:hidden mb-8"
          >
            <VideoReels variant="horizontal" />
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center overflow-visible">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease }}
              className="max-w-xl"
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.1] mb-5 lg:mb-8" data-testid="hero-heading">
                Your next setup,
                <br />
                personally crafted.
              </h1>
              <p className="text-sm md:text-base lg:text-lg text-muted-foreground leading-relaxed mb-6 lg:mb-10 max-w-md" data-testid="hero-subheading">
                A sincere, hands-on service that takes the guesswork out of PC building.
                From component sourcing to final assembly — considered at every step.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/build-choice">
                  <Button
                    size="lg"
                    className="h-11 lg:h-12 px-6 lg:px-8 font-medium btn-scale rounded-full text-sm"
                    data-testid="hero-builder-btn"
                  >
                    Start Building
                    <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-11 lg:h-12 px-6 lg:px-8 font-medium btn-scale rounded-full text-sm"
                    data-testid="hero-services-btn"
                  >
                    <Wrench className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Browse Services
                  </Button>
                </Link>
                <Link to="/advice">
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-11 lg:h-12 px-6 lg:px-8 font-medium btn-scale rounded-full text-sm"
                    data-testid="hero-advice-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Get Advice
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Desktop: carousel reel */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2, ease }}
              className="hidden lg:flex justify-center items-center overflow-visible"
            >
              <VideoReels />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Browse Options */}
      <section className="py-16 md:py-20" data-testid="browse-section">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease }}
              viewport={{ once: true }}
            >
              <Link to="/builder" className="block">
                <div className="border border-black/5 bg-card rounded-2xl p-8 hover-lift h-full" data-testid="browse-parts">
                  <ShoppingBag className="w-7 h-7 mb-4 text-foreground/60" strokeWidth={1.5} />
                  <h3 className="text-xl font-medium mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Browse Parts</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    Explore our curated selection of CPUs, GPUs, motherboards, RAM and more. Compare prices across Singapore's top retailers.
                  </p>
                  <span className="text-sm font-medium flex items-center gap-1 text-foreground">
                    View Parts <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1, ease }}
              viewport={{ once: true }}
            >
              <Link to="/services" className="block">
                <div className="border border-black/5 bg-card rounded-2xl p-8 hover-lift h-full" data-testid="browse-services">
                  <Wrench className="w-7 h-7 mb-4 text-foreground/60" strokeWidth={1.5} />
                  <h3 className="text-xl font-medium mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Browse Services</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    PC Cleaning from $20 · Cable Management from $30 · Build & Assembly from $60.
                    Book a 4-hour slot at your convenience.
                  </p>
                  <span className="text-sm font-medium flex items-center gap-1 text-foreground">
                    View Services <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-24 md:py-32" data-testid="features-section">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="text-sm tracking-widest uppercase text-muted-foreground mb-3">What we offer</p>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tight">
              Thoughtfully built, every time.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Compatibility Assured",
                desc: "Every component is verified to work together seamlessly — no surprises, no guesswork.",
                testid: "feature-card-compatibility"
              },
              {
                icon: Zap,
                title: "Best Prices Sourced",
                desc: "Components sourced across Amazon, Newegg, Shopee, Lazada and Carousell for competitive pricing.",
                testid: "feature-card-marketplace"
              },
              {
                icon: Cpu,
                title: "Expert Guidance",
                desc: "AI-assisted recommendations paired with personal expertise to match your needs and budget.",
                testid: "feature-card-ai"
              }
            ].map((feature, idx) => (
              <motion.div
                key={feature.testid}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1, ease }}
                viewport={{ once: true }}
                className="border border-black/5 bg-card rounded-2xl p-10 hover-lift"
                data-testid={feature.testid}
              >
                <feature.icon className="w-8 h-8 mb-6 text-foreground/70" strokeWidth={1.5} />
                <h3 className="text-xl font-medium mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Teaser */}
      <section className="py-24 md:py-32 bg-muted/50" data-testid="gallery-teaser-section">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease }}
              viewport={{ once: true }}
              className="lg:col-span-5"
            >
              <p className="text-sm tracking-widest uppercase text-muted-foreground mb-3">Past work</p>
              <h2 className="text-4xl sm:text-5xl font-light tracking-tight mb-6">
                Builds we're proud of.
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8 max-w-md">
                Each build is a collaboration. Browse setups we've completed and get inspired for yours.
              </p>
              <Link to="/gallery">
                <Button variant="outline" className="rounded-full h-11 px-6 btn-scale" data-testid="gallery-cta-btn">
                  View Gallery
                  <ArrowUpRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease }}
              viewport={{ once: true }}
              className="lg:col-span-7"
            >
              <div className="rounded-2xl overflow-hidden aspect-[16/10]">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/224a10f5-795e-48d2-8ad7-652c431905ce/images/ef4b8e9d41dbd7bb93fdd5972a3beaac2b5aeab2604ddee3549c7e5aa18fe850.png"
                  alt="Curated workspace build"
                  className="w-full h-full object-cover"
                  data-testid="gallery-teaser-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-24 md:py-32" data-testid="about-section">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-widest uppercase text-muted-foreground mb-3">About</p>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tight mb-10" data-testid="about-heading">
              The person behind the builds.
            </h2>
            <div className="text-muted-foreground leading-relaxed space-y-5 text-base">
              <p>
                I started Mid2Mint because I believe everyone deserves a PC that's built with care — not
                just assembled from a parts list. Based in Singapore, I know the local market inside out.
              </p>
              <p>
                Whether it's finding the right deal on Carousell or selecting the perfect balance of
                performance and aesthetics, every build gets my full attention. No upselling, no shortcuts.
              </p>
              <p>
                From first-time builders to seasoned enthusiasts — I'm here to make the process
                straightforward, enjoyable, and genuinely personal.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-muted/50" data-testid="cta-section">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-light tracking-tight mb-6" data-testid="cta-heading">
              Ready to start?
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-10 max-w-md mx-auto">
              Use the builder to configure your own setup, or tell me what you need and I'll
              put together something tailored for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/build-choice">
                <Button
                  size="lg"
                  className="h-12 px-8 font-medium btn-scale rounded-full"
                  data-testid="cta-get-started-btn"
                >
                  Start Building
                  <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
                </Button>
              </Link>
              <Link to="/custom-build">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 px-8 font-medium btn-scale rounded-full"
                  data-testid="cta-custom-btn"
                >
                  Request Custom Build
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
